import type { JSX } from 'react';
import { useState } from 'react';

import { Loader2, PlayCircle, Save, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

import { type AlertAction, type AlertType, isNotifyWebhookAlertAction } from '../../types/alertsConfig';
import AlertActionRow from './AlertActionRow';
import NotifyWebhookActionRow from './NotifyWebhookActionRow';

interface AlertRuleTestState {
  testText: string;
  result: string | null;
}

interface AlertRuleEditorHandlers {
  onAlertChange: (alertIndex: number, key: keyof AlertType, value: string) => void;
  onAddExample: (alertIndex: number) => void;
  onExampleChange: (alertIndex: number, exampleIndex: number, value: string) => void;
  onDeleteExample: (alertIndex: number, exampleIndex: number) => void;
  onAddAction: (alertIndex: number) => void;
  onActionChange: (alertIndex: number, actionIndex: number, key: keyof AlertAction, value: string) => void;
  onActionParameterChange: (
    alertIndex: number,
    actionIndex: number,
    paramKey: string,
    paramValue: string | number | boolean | null
  ) => void;
  onDeleteAction: (alertIndex: number, actionIndex: number) => void;
  onToggleStandard: (alertIndex: number) => void;
  onDeleteAlert: (alertIndex: number) => void;
  onTestChange: (alertIndex: number, value: string) => void;
  onTestAlert: (alertIndex: number) => void;
  onSave: () => void;
}

interface AlertRuleEditorProps {
  alert: AlertType | null;
  alertIndex: number | null;
  projectId: number;
  testState: AlertRuleTestState | null;
  isSaving: boolean;
  handlers: AlertRuleEditorHandlers;
}

const getEditorTitle = (alert: AlertType, alertIndex: number): string => {
  if (alert.id.trim()) {
    return alert.id.trim();
  }

  if (alert.description.trim()) {
    return `Regla ${alertIndex + 1}`;
  }

  return 'Nueva regla';
};

const AlertRuleEditor = ({
  alert,
  alertIndex,
  projectId,
  testState,
  isSaving,
  handlers,
}: AlertRuleEditorProps): JSX.Element => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  if (alert === null || alertIndex === null) {
    return (
      <section
        className="flex min-h-[28rem] flex-col items-center justify-center bg-bg-canvas px-6 py-10 text-center"
        aria-label="Editor de regla"
      >
        <div className="max-w-md">
          <p className="m-0 text-xs font-bold uppercase tracking-widest text-text-subtle font-body">
            Sin regla seleccionada
          </p>
          <h3 className="m-0 mt-2 text-xl font-bold text-brand-navy font-display">
            Selecciona una regla o crea una nueva
          </h3>
          <p className="m-0 mt-2 text-sm text-text-muted font-body">
            El editor se abrirá aquí para configurar la descripción, ejemplos, acciones y prueba.
          </p>
        </div>
      </section>
    );
  }

  const title = getEditorTitle(alert, alertIndex);
  const isStandard = alert.standard === true;
  const alertIdInputId = `alert-${alertIndex}-id`;
  const alertDescriptionInputId = `alert-${alertIndex}-description`;
  const testTextInputId = `alert-${alertIndex}-test-text`;

  return (
    <section className="flex min-h-[560px] min-w-0 flex-col bg-bg-canvas" aria-label="Editor de regla">
      <div className="min-h-0 flex-1 overflow-auto px-6 py-5">
        <div className="mb-5">
          <p className="m-0 text-xs font-bold uppercase tracking-widest text-text-subtle font-body">
            Editando · {title}
          </p>
          <h3 className="m-0 mt-1 text-xl font-bold text-brand-navy font-display">{title}</h3>
          <p className="m-0 mt-1 text-xs text-text-muted font-body">
            {isStandard
              ? 'Las alertas estándar las configura y optimiza LIDZ para identificar cada caso con la mayor precisión. Aquí solo puedes activarla o desactivarla y configurar sus acciones.'
              : 'Los cambios se guardan en la configuración de alertas del proyecto maestro.'}
          </p>
        </div>

        <div className="space-y-5">
          {isStandard ? (
            <>
              <div className="flex items-center gap-2.5">
                <Switch
                  aria-label="Activar alerta estándar"
                  checked={!alert.disabled}
                  onCheckedChange={() => handlers.onToggleStandard(alertIndex)}
                />
                <span className="text-sm text-text-default font-body">{alert.disabled ? 'Desactivada' : 'Activa'}</span>
              </div>
              <p className="rounded-lg border border-border-subtle bg-bg-surface px-4 py-3 text-sm text-text-default font-body">
                {alert.description}
              </p>
            </>
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,0.75fr)_minmax(0,1fr)]">
              <label className="block" htmlFor={alertIdInputId}>
                <span className="mb-1 block text-sm font-medium text-text-muted font-body">ID</span>
                <Input
                  id={alertIdInputId}
                  value={alert.id}
                  onChange={(event) => handlers.onAlertChange(alertIndex, 'id', event.target.value)}
                  className="h-10 px-4 py-2"
                  placeholder="cliente_pide_ejecutivo"
                />
              </label>

              <label className="block" htmlFor={alertDescriptionInputId}>
                <span className="mb-1 block text-sm font-medium text-text-muted font-body">Descripción</span>
                <Textarea
                  id={alertDescriptionInputId}
                  value={alert.description}
                  onChange={(event) => handlers.onAlertChange(alertIndex, 'description', event.target.value)}
                  rows={3}
                  className="min-h-[6rem] resize-y"
                  placeholder="Describe cuándo debe activarse esta alerta"
                />
              </label>
            </div>
          )}

          {!isStandard && (
            <section aria-labelledby="alert-examples-heading" className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 id="alert-examples-heading" className="m-0 text-sm font-semibold text-text-default font-body">
                  Ejemplos
                </h4>
                <Button type="button" variant="outline" size="sm" onClick={() => handlers.onAddExample(alertIndex)}>
                  Agregar ejemplo
                </Button>
              </div>

              {alert.examples.length === 0 ? (
                <p className="m-0 rounded-lg border border-dashed border-border-subtle px-3 py-3 text-sm text-text-muted font-body">
                  Sin ejemplos configurados.
                </p>
              ) : (
                <div className="space-y-2">
                  {alert.examples.map((example, exampleIndex) => {
                    const exampleInputId = `alert-${alertIndex}-example-${exampleIndex}`;

                    return (
                      // Examples are plain strings from the API and do not expose stable ids.
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={`example-${exampleIndex}`} className="flex items-center gap-2">
                        <Input
                          id={exampleInputId}
                          value={example}
                          onChange={(event) => {
                            handlers.onExampleChange(alertIndex, exampleIndex, event.target.value);
                          }}
                          className="h-10 flex-1 px-4 py-2"
                          aria-label={`Ejemplo ${exampleIndex + 1}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handlers.onDeleteExample(alertIndex, exampleIndex)}
                          aria-label={`Eliminar ejemplo ${exampleIndex + 1}`}
                        >
                          <Trash2 className="size-4" aria-hidden />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          <section aria-labelledby="alert-actions-heading" className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 id="alert-actions-heading" className="m-0 text-sm font-semibold text-text-default font-body">
                Acciones
              </h4>
              <Button type="button" variant="outline" size="sm" onClick={() => handlers.onAddAction(alertIndex)}>
                Agregar acción
              </Button>
            </div>

            {(alert.actions ?? []).length === 0 ? (
              <p className="m-0 rounded-lg border border-dashed border-border-subtle px-3 py-3 text-sm text-text-muted font-body">
                Sin acciones configuradas.
              </p>
            ) : (
              <div className="space-y-2">
                {alert.actions?.map((action, actionIndex) => {
                  if (isNotifyWebhookAlertAction(action.function)) {
                    const webhookId = action.parameters?.webhookId;
                    const selectedWebhookId = typeof webhookId === 'number' ? webhookId : null;

                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <NotifyWebhookActionRow
                        key={`action-${actionIndex}`}
                        actionIndex={actionIndex}
                        alertIndex={alertIndex}
                        projectId={projectId}
                        selectedWebhookId={selectedWebhookId}
                        onWebhookChange={(id) =>
                          handlers.onActionParameterChange(alertIndex, actionIndex, 'webhookId', id)
                        }
                        onDelete={() => handlers.onDeleteAction(alertIndex, actionIndex)}
                      />
                    );
                  }

                  return (
                    // Actions are persisted as function strings and do not expose stable ids.
                    // eslint-disable-next-line react/no-array-index-key
                    <AlertActionRow
                      key={`action-${actionIndex}`}
                      action={action}
                      actionIndex={actionIndex}
                      alertIndex={alertIndex}
                      onFunctionChange={(fn) => handlers.onActionChange(alertIndex, actionIndex, 'function', fn)}
                      onParameterChange={(paramKey, paramValue) =>
                        handlers.onActionParameterChange(alertIndex, actionIndex, paramKey, paramValue)
                      }
                      onDelete={() => handlers.onDeleteAction(alertIndex, actionIndex)}
                    />
                  );
                })}
              </div>
            )}
          </section>

          {!isStandard && (
            <section aria-labelledby="alert-test-heading" className="space-y-2 border-t border-border-subtle pt-4">
              <h4 id="alert-test-heading" className="m-0 text-sm font-semibold text-text-default font-body">
                Probar con un mensaje
              </h4>
              <label className="block" htmlFor={testTextInputId}>
                <span className="mb-1 block text-sm font-medium text-text-muted font-body">Texto de prueba</span>
                <Textarea
                  id={testTextInputId}
                  value={testState?.testText ?? ''}
                  onChange={(event) => handlers.onTestChange(alertIndex, event.target.value)}
                  rows={2}
                  className="min-h-[5rem] resize-y"
                  placeholder="Pega un mensaje de ejemplo"
                />
              </label>
              {testState?.result ? (
                <div className="rounded-lg border border-border-subtle bg-bg-surface p-4 text-sm font-body">
                  <p className="m-0 mb-1 font-semibold text-text-default">Resultado:</p>
                  <p className="m-0 whitespace-pre-wrap text-text-muted">{testState.result}</p>
                </div>
              ) : (
                <p className="m-0 text-xs text-text-subtle font-body">
                  El resultado aparecerá aquí después de pulsar “Probar”.
                </p>
              )}
            </section>
          )}
        </div>
      </div>

      <div
        className="sticky bottom-0 flex flex-col gap-3 border-t border-border-subtle bg-bg-surface/95 px-6 py-3
          shadow-save-bar backdrop-blur sm:flex-row sm:items-center sm:justify-between"
      >
        {!isStandard && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-fit gap-1.5 text-error hover:text-error"
            disabled={isSaving}
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            <Trash2 className="size-3.5 shrink-0" aria-hidden />
            Eliminar regla
          </Button>
        )}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="break-words">¿Eliminar “{title}”?</AlertDialogTitle>
              <AlertDialogDescription className="break-words">
                La regla se eliminará del proyecto de inmediato. Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  handlers.onDeleteAlert(alertIndex);
                }}
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          {!isStandard && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => handlers.onTestAlert(alertIndex)}
            >
              <PlayCircle className="size-3.5 shrink-0" aria-hidden />
              Probar
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            disabled={isSaving}
            className="gap-1.5 bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
            onClick={handlers.onSave}
          >
            {isSaving ? (
              <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
            ) : (
              <Save className="size-3.5 shrink-0" aria-hidden />
            )}
            Guardar cambios
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AlertRuleEditor;
